import React, { useRef } from 'react';
import { Download } from 'lucide-react';

const VulcanBadge = ({ 
  assessmentType = 'HIPAA', 
  assessmentName = 'Assessment',
  dateAchieved = new Date().toLocaleDateString(),
  showDownload = true,
  size = 'large' // 'small', 'medium', 'large'
}) => {
  const badgeRef = useRef(null);

  const sizeClasses = {
    small: 'w-32 h-32',
    medium: 'w-48 h-48',
    large: 'w-64 h-64'
  };

  const textSizes = {
    small: { title: 'text-xs', subtitle: 'text-[8px]', type: 'text-[10px]' },
    medium: { title: 'text-sm', subtitle: 'text-[10px]', type: 'text-xs' },
    large: { title: 'text-lg', subtitle: 'text-xs', type: 'text-sm' }
  };

  const downloadBadge = async () => {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const size = 800; // Square social media size
    canvas.width = size;
    canvas.height = size;

    // Background gradient
    const gradient = ctx.createLinearGradient(0, 0, size, size);
    gradient.addColorStop(0, '#1e1b4b'); // violet-950
    gradient.addColorStop(0.5, '#4c1d95'); // violet-900
    gradient.addColorStop(1, '#1e1b4b');
    ctx.fillStyle = gradient;
    ctx.fillRect(0, 0, size, size);

    // Outer ring
    ctx.beginPath();
    ctx.arc(size/2, size/2, 350, 0, Math.PI * 2);
    ctx.strokeStyle = '#a78bfa'; // violet-400
    ctx.lineWidth = 8;
    ctx.stroke();

    // Inner ring
    ctx.beginPath();
    ctx.arc(size/2, size/2, 320, 0, Math.PI * 2);
    ctx.strokeStyle = '#7c3aed'; // violet-600
    ctx.lineWidth = 4;
    ctx.stroke();

    // Shield background
    ctx.beginPath();
    ctx.arc(size/2, size/2 - 40, 120, 0, Math.PI * 2);
    ctx.fillStyle = 'rgba(139, 92, 246, 0.3)';
    ctx.fill();

    // Vulcan salute emoji
    ctx.font = '120px serif';
    ctx.textAlign = 'center';
    ctx.fillText('🖖', size/2 - 60, size/2 + 10);

    // Shield emoji
    ctx.font = '80px serif';
    ctx.fillText('🛡️', size/2 + 50, size/2 + 30);

    // Title
    ctx.fillStyle = '#ffffff';
    ctx.font = 'bold 48px system-ui, -apple-system, sans-serif';
    ctx.textAlign = 'center';
    ctx.fillText('VULCAN LEVEL', size/2, size/2 + 120);
    
    ctx.fillStyle = '#a78bfa';
    ctx.font = 'bold 56px system-ui, -apple-system, sans-serif';
    ctx.fillText('SECURITY', size/2, size/2 + 175);

    // Tagline
    ctx.fillStyle = '#c4b5fd';
    ctx.font = '24px system-ui, -apple-system, sans-serif';
    ctx.fillText('Live Long & Stay Secure', size/2, size/2 + 220);

    // Assessment type badge
    const typeColors = {
      'HIPAA': '#22c55e',
      'CMMC': '#3b82f6',
      'NIST_CSF': '#a855f7',
      'FINRA': '#f59e0b',
      'SOC2': '#06b6d4',
      'SOC2_TYPE2': '#14b8a6'
    };
    const typeColor = typeColors[assessmentType] || '#6366f1';
    
    ctx.fillStyle = typeColor;
    ctx.beginPath();
    ctx.roundRect(size/2 - 100, size/2 + 245, 200, 36, 18);
    ctx.fill();
    
    ctx.fillStyle = '#ffffff';
    ctx.font = 'bold 20px system-ui, -apple-system, sans-serif';
    const displayType = assessmentType === 'NIST_CSF' ? 'NIST CSF' : 
                        assessmentType === 'SOC2' ? 'SOC 2 Type 1' :
                        assessmentType === 'SOC2_TYPE2' ? 'SOC 2 Type 2' : assessmentType;
    ctx.fillText(`${displayType} CERTIFIED`, size/2, size/2 + 270);

    // Date
    ctx.fillStyle = '#94a3b8';
    ctx.font = '18px system-ui, -apple-system, sans-serif';
    ctx.fillText(`100% Compliance Achieved`, size/2, size/2 + 310);
    ctx.fillText(dateAchieved, size/2, size/2 + 335);

    // Aspiritech branding
    ctx.fillStyle = '#64748b';
    ctx.font = '16px system-ui, -apple-system, sans-serif';
    ctx.fillText('Aspiritech • Masks Down, Shields Up', size/2, size - 40);

    // Download
    const link = document.createElement('a');
    link.download = `vulcan-security-badge-${assessmentType.toLowerCase()}.jpg`;
    link.href = canvas.toDataURL('image/jpeg', 0.95);
    link.click();
  };

  const typeColors = {
    'HIPAA': 'from-green-500 to-green-600',
    'CMMC': 'from-blue-500 to-blue-600',
    'NIST_CSF': 'from-purple-500 to-purple-600',
    'FINRA': 'from-amber-500 to-amber-600',
    'SOC2': 'from-cyan-500 to-cyan-600',
    'SOC2_TYPE2': 'from-teal-500 to-teal-600'
  };

  const getDisplayType = (type) => {
    const displayMap = {
      'NIST_CSF': 'NIST CSF',
      'SOC2': 'SOC 2 Type 1',
      'SOC2_TYPE2': 'SOC 2 Type 2'
    };
    return displayMap[type] || type;
  };

  return (
    <div className="flex flex-col items-center gap-4">
      {/* Badge Display */}
      <div 
        ref={badgeRef}
        className={`${sizeClasses[size]} relative bg-gradient-to-br from-violet-950 via-violet-900 to-violet-950 rounded-full flex flex-col items-center justify-center border-4 border-violet-400 shadow-2xl shadow-violet-500/30`}
      >
        {/* Inner ring */}
        <div className="absolute inset-3 rounded-full border-2 border-violet-600" />
        
        {/* Icons */}
        <div className="flex items-center gap-1 mb-1">
          <span className={size === 'small' ? 'text-2xl' : size === 'medium' ? 'text-4xl' : 'text-5xl'}>🖖</span>
          <span className={size === 'small' ? 'text-xl' : size === 'medium' ? 'text-3xl' : 'text-4xl'}>🛡️</span>
        </div>
        
        {/* Title */}
        <div className="text-center">
          <p className={`font-bold text-white ${textSizes[size].title}`}>VULCAN LEVEL</p>
          <p className={`font-bold text-violet-400 ${textSizes[size].title}`}>SECURITY</p>
        </div>
        
        {/* Tagline */}
        <p className={`text-violet-300 mt-1 ${textSizes[size].subtitle}`}>Live Long & Stay Secure</p>
        
        {/* Type Badge */}
        <div className={`mt-2 px-3 py-0.5 bg-gradient-to-r ${typeColors[assessmentType] || 'from-indigo-500 to-indigo-600'} rounded-full`}>
          <span className={`text-white font-semibold ${textSizes[size].type}`}>
            {getDisplayType(assessmentType)}
          </span>
        </div>
      </div>

      {/* Download Button */}
      {showDownload && (
        <button
          onClick={downloadBadge}
          className="flex items-center gap-2 px-4 py-2 bg-violet-600 hover:bg-violet-700 text-white rounded-lg transition-colors text-sm"
        >
          <Download className="w-4 h-4" />
          Download Badge
        </button>
      )}
    </div>
  );
};

export default VulcanBadge;
